import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useGetSharedPublicLayerQuery, useGetSharedPublicFeedQuery } from '../../api/client';
import ShareMapFullscreen from './components/ShareMapFullscreen';
import ShareDrawer from './components/ShareDrawer';
import AppBanner from './components/AppBanner';
import '../../App.css';

export default function ShareLayerPage(): React.ReactElement {
  const { shareId } = useParams<{ shareId: string }>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);

  // Fetch layer data
  const {
    data: layerData,
    isLoading: isLoadingLayer,
    error: layerError
  } = useGetSharedPublicLayerQuery(shareId || '', {
    skip: !shareId
  });

  // Fetch layer feed
  const {
    data: feedData,
    isLoading: isLoadingFeed,
    error: feedError
  } = useGetSharedPublicFeedQuery(
    { shareId: shareId || '', sortBy: 'recent', size: 100, page: 1 },
    { skip: !shareId }
  );

  const isLoading = isLoadingLayer || isLoadingFeed;
  const error = layerError || feedError;

  const handleMarkerClick = useCallback((index: number) => {
    setCurrentPostIndex(index);
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerMinimize = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handlePostChange = useCallback((index: number) => {
    setCurrentPostIndex(index);
  }, []);

  if (!shareId) {
    return (
      <div className="share-page error-state">
        <h1>Invalid Layer Link</h1>
        <p>The layer link appears to be invalid.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="share-page loading-state">
        <div className="loading-spinner">Loading layer...</div>
      </div>
    );
  }

  if (error || !feedData || !feedData.items || feedData.items.length === 0) {
    return (
      <div className="share-page error-state">
        <h1>Layer Not Found</h1>
        <p>This layer may be private, empty, or no longer available.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  const posts = feedData.items;

  return (
    <div className="share-page-fullscreen">
      {/* App Banner */}
      <AppBanner />

      {/* Fullscreen Map */}
      <ShareMapFullscreen
        posts={posts}
        currentPostIndex={currentPostIndex}
        onMarkerClickWithIndex={handleMarkerClick}
        layer={layerData}
      />

      {/* Bottom Drawer with Carousel */}
      <ShareDrawer
        posts={posts}
        isOpen={isDrawerOpen}
        onMinimize={handleDrawerMinimize}
        onExpand={() => setIsDrawerOpen(true)}
        onPostChange={handlePostChange}
        currentPostIndex={currentPostIndex}
      />
    </div>
  );
}
